import { useState } from 'react';

import type { InventoryEntryOut, InventoryEntryType, InventorySummaryOut } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, formatDateTime, useAsync } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 재고 목록 — 레거시 inventory/list + detail 파리티.
 *
 * 왼쪽은 단말별 요약(입고/출고/재고), 행 선택 시 오른쪽에 해당 단말의 입출고
 * 이력이 열린다. 이력의 자동출고(status='auto')는 주문 완료 처리 시 서버가
 * 만든 행이라 수정 대상에서 제외한다 — 주문과의 정합성이 깨진다.
 * 회사 격리(자기 회사 입출고만 집계)는 서버가 강제한다.
 */

const SIZE = 30;

export const INVENTORY_TYPE_LABEL: Record<InventoryEntryType, string> = {
  add: '입고',
  minus: '출고',
};

/** 구분 표시 — 자동출고는 주문 완료가 만든 행이라 수기 출고와 구별해 보여준다. */
export function entryLabel(entry: Pick<InventoryEntryOut, 'type' | 'status'>): string {
  if (entry.status === 'auto') return '자동출고';
  return INVENTORY_TYPE_LABEL[entry.type as InventoryEntryType] ?? entry.type;
}

export function InventoryManage() {
  const [typeId, setTypeId] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<InventorySummaryOut | null>(null);
  // 이력에서 입출고가 등록·수정되면 요약 합계도 다시 읽어야 한다.
  const [version, setVersion] = useState(0);

  const types = useAsync(() => api.listTerminalTypes(), []);
  const list = useAsync(
    () =>
      api.listInventory({
        terminal_type_id: typeId === '' ? undefined : typeId,
        page,
        size: SIZE,
      }),
    [typeId, page, version],
  );

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <button
            type="button"
            className={typeId === '' ? 'chip active' : 'chip'}
            onClick={() => {
              setTypeId('');
              setPage(1);
            }}
          >
            전체
          </button>
          {(types.data ?? []).map((t) => (
            <button
              key={t.id}
              type="button"
              className={typeId === t.id ? 'chip active' : 'chip'}
              onClick={() => {
                setTypeId(t.id);
                setPage(1);
              }}
            >
              {t.name}
            </button>
          ))}
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>타입</th>
                <th>단말 명칭</th>
                <th>모델</th>
                <th className="num">입고 갯수</th>
                <th className="num">출고 갯수</th>
                <th className="num">토탈 갯수</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    등록된 단말이 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((row) => (
                <tr
                  key={row.def_terminal_id}
                  className={row.def_terminal_id === selected?.def_terminal_id ? 'selected' : ''}
                  onClick={() => setSelected(row)}
                >
                  <td>{row.type_name ?? '-'}</td>
                  <td>{row.name}</td>
                  <td>{row.model ?? '-'}</td>
                  <td className="num">{row.add_total.toLocaleString()}</td>
                  <td className="num">{row.minus_total.toLocaleString()}</td>
                  <td className="num">{row.stock.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>

      <aside className="admin__aside">
        {selected === null ? (
          <p className="muted">단말을 선택하세요.</p>
        ) : (
          <InventoryEntries
            key={selected.def_terminal_id}
            terminal={selected}
            onClose={() => setSelected(null)}
            onChanged={() => setVersion((v) => v + 1)}
          />
        )}
      </aside>
    </div>
  );
}

/** 단말 하나의 입출고 이력 + 등록/수정 (레거시 InventoryDetail 파리티). */
function InventoryEntries({
  terminal,
  onClose,
  onChanged,
}: {
  terminal: InventorySummaryOut;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [entryType, setEntryType] = useState<InventoryEntryType | ''>('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<InventoryEntryOut | null>(null);

  const entries = useAsync(
    () =>
      api.listInventoryEntries(terminal.def_terminal_id, {
        entry_type: entryType === '' ? undefined : entryType,
        page,
        size: SIZE,
      }),
    [terminal.def_terminal_id, entryType, page],
  );

  const total = entries.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>
          {terminal.name} {terminal.model ? `(${terminal.model})` : ''}
        </h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      {/* 합계 — 필터가 합계에도 그대로 걸리는 레거시 산식(서버 응답 그대로). */}
      {entries.data && (
        <section className="panel">
          <div className="field">
            <span className="field__label">입고 갯수</span>
            <span className="field__value">{entries.data.add_total.toLocaleString()}</span>
          </div>
          <div className="field">
            <span className="field__label">출고 갯수</span>
            <span className="field__value">{entries.data.minus_total.toLocaleString()}</span>
          </div>
          <div className="field">
            <span className="field__label">토탈 갯수</span>
            <span className="field__value">{entries.data.stock.toLocaleString()}</span>
          </div>
        </section>
      )}

      <h3 className="section">입출고 이력</h3>
      <div className="toolbar">
        {(
          [
            ['', '전체'],
            ['add', '입고'],
            ['minus', '출고'],
          ] as Array<[InventoryEntryType | '', string]>
        ).map(([value, label]) => (
          <button
            key={value || 'all'}
            type="button"
            className={entryType === value ? 'chip active' : 'chip'}
            onClick={() => {
              setEntryType(value);
              setPage(1);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {entries.loading && <Spinner />}
      {entries.error && <ErrorBox message={entries.error} onRetry={entries.reload} />}

      {entries.data && (
        <table className="grid">
          <thead>
            <tr>
              <th>구분</th>
              <th className="num">갯수</th>
              <th>등록일시</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.data.items.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  입출고 이력이 없습니다.
                </td>
              </tr>
            )}
            {entries.data.items.map((e) => (
              <tr key={e.id}>
                <td>
                  <span className={e.status === 'auto' ? 'badge s2' : e.type === 'add' ? 'badge s4' : 'badge s5'}>
                    {entryLabel(e)}
                  </span>
                </td>
                <td className="num">{e.count.toLocaleString()}</td>
                <td>{formatDateTime(e.created_at)}</td>
                <td>
                  {e.status !== 'auto' && (
                    <button type="button" className="link" onClick={() => setEditing(e)}>
                      수정
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="pager">
        <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
          이전
        </button>
        <span>
          {page} / {lastPage} (총 {total}건)
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
          다음
        </button>
      </div>

      <EntryCreateForm
        terminalId={terminal.def_terminal_id}
        onCreated={() => {
          entries.reload();
          onChanged();
        }}
      />

      {editing && (
        <EntryEditModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            entries.reload();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** 입고/출고 수기 등록 — 레거시 inventory/edit(InventoryAdd) 파리티. */
function EntryCreateForm({
  terminalId,
  onCreated,
}: {
  terminalId: number;
  onCreated: () => void;
}) {
  const [entryType, setEntryType] = useState<InventoryEntryType>('add');
  const [count, setCount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function submit() {
    const n = Number(count);
    if (!Number.isInteger(n) || n < 1) {
      setError('갯수는 1 이상의 정수여야 합니다.');
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api.createInventoryEntry({
        def_terminal_id: terminalId,
        entry_type: entryType,
        count: n,
      });
      setCount('');
      setOk('등록했습니다.');
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h3 className="section">입출고 등록</h3>
      <section className="panel">
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <select
            value={entryType}
            onChange={(e) => setEntryType(e.target.value as InventoryEntryType)}
          >
            <option value="add">입고</option>
            <option value="minus">출고</option>
          </select>
          <input
            type="number"
            min={1}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            placeholder="갯수"
          />
          <button type="submit" className="primary" disabled={busy}>
            {busy ? '등록 중…' : '등록'}
          </button>
        </form>
        {error && <p className="error">{error}</p>}
        {ok && <p className="ok">{ok}</p>}
      </section>
    </>
  );
}

/** 수기 이력 수정 — 레거시는 등록과 같은 폼으로 덮어썼다(InventoryAdd id 분기). */
function EntryEditModal({
  entry,
  onClose,
  onSaved,
}: {
  entry: InventoryEntryOut;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [entryType, setEntryType] = useState<InventoryEntryType>(
    entry.type === 'minus' ? 'minus' : 'add',
  );
  const [count, setCount] = useState(String(entry.count));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const n = Number(count);
    if (!Number.isInteger(n) || n < 1) {
      setError('갯수는 1 이상의 정수여야 합니다.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.updateInventoryEntry(entry.id, { entry_type: entryType, count: n });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="입출고 수정" onClose={onClose}>
      <label className="labeled">
        구분 *
        <select
          value={entryType}
          onChange={(e) => setEntryType(e.target.value as InventoryEntryType)}
        >
          <option value="add">입고</option>
          <option value="minus">출고</option>
        </select>
      </label>
      <label className="labeled">
        갯수 *
        <input type="number" min={1} value={count} onChange={(e) => setCount(e.target.value)} />
      </label>

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>
    </Modal>
  );
}
